import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "./Header.tsx";
import Sidebar from "./Sidebar.tsx";
import styles from "./PumpDashboard.module.css";
import useTheme from "../hooks/useTheme.ts";
import { useNavigate, useLocation } from "react-router-dom";
import { sidebarOpenForViewport } from "../context/SidebarProvider.tsx";

interface Pad {
  id: number;
  pad_name: string;
  fleet: number;
  status: string;
  stage_count: number;
  well_count: number;
  start_ts: number | null;
}

interface FleetGroup {
  fleet: number;
  latestPad: Pad;
}

interface PumpRow {
  fleet: number;
  pump_name: string;
  controller_type?: number | null;
  pump_type?: "diesel" | "electric";
  station?: number | string | null;
  engine_hours?: number | null;
  trans_hours?: number | null;
  fe_hours?: number | null;
  throttle?: number | null;
  rpm?: number | null;
  engine_coolant_temp?: number | null;
  engine_temp?: number | null;
  engine_oil_pressure?: number | null;
  powerend_lube_pressure?: number | string | null;
  powerend_oil_pressure?: number | null;
  powerend_oil_temperature?: number | null;
  engine_oil_temperature?: number | null;
  engine_fuel_rate?: number | null;
  engine_load?: number | null;
  trans_gear?: number | string | null;
  trans_oil_pressure?: number | string | null;
  trans_oil_temp?: number | null;
  trans_output_rpm?: number | null;
  suction_pressure?: number | null;
  discharge_pressure?: number | null;
  df_substitution?: number | null;
  df_displacement?: number | null;
  calculated_discharge_rate?: number | null;
  motor_rpm?: number | null;
  dc_link_volts?: number | null;
  motor_current?: number | null;
  winding_a_temp?: number | null;
  winding_b_temp?: number | null;
  winding_c_temp?: number | null;
  de_bearing_temp?: number | null;
  nde_bearing_temp?: number | null;
  target_rate?: number | null;
  igbt_1_temp?: number | null;
  igbt_2_temp?: number | null;
  status: string;
  pump_timestamp?: number | null;
}

type BlenderDashboardPreviewRow = {
  agent_name: string;
  blender_type: "Diesel" | "Electric";
  status: "Active" | "Inactive" | "Lost Comm";
  density: number;
  discharge_rate: number;
  suction_rate: number;
  auger_rpm: number;
  last_updated: string;
};

// Frontend-only rows for the first Blender Dashboard layout review. The real
// backend will replace these after Blender routing/database work is available.
const buildBlenderDashboardPreviewRows = (): BlenderDashboardPreviewRow[] => [
  {
    agent_name: "B18047",
    blender_type: "Diesel",
    status: "Active",
    density: 8.6,
    discharge_rate: 62.5,
    suction_rate: 61.8,
    auger_rpm: 121,
    last_updated: "--",
  },
  {
    agent_name: "EFB23001A",
    blender_type: "Electric",
    status: "Active",
    density: 8.4,
    discharge_rate: 60.8,
    suction_rate: 60.1,
    auger_rpm: 118,
    last_updated: "--",
  },
];

type ThresholdSeverity = "normal" | "warning" | "critical";

interface PumpInterlockCondition {
  channel: string;
  operator: string;
  value: number;
}

interface PumpInterlockRule {
  channel: string;
  severity: "warning" | "critical";
  operator: string;
  value: number;
  conditions?: PumpInterlockCondition[];
}

const REFRESH_INTERVAL_MS = 2000;
const LOST_COMM_MS = 60 * 1000; // Frontend fallback only when backend status is unavailable
const ENGINE_RUNNING_RPM = 500;

// Keep this configurable. Update it when the confirmed JDD
// engine-oil-pressure kick-out setting changes.
const ENGINE_OIL_KICKOUT_PSI = 60;
const ENGINE_OIL_WARNING_MARGIN_PSI = 4;

const PumpDashboard: React.FC = () => {
  const [pumpRows, setPumpRows] = useState<PumpRow[]>([]);
  const [searchText, setSearchText] = useState("");
  const [fleetSearchText, setFleetSearchText] = useState("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [assetStatusPump, setAssetStatusPump] = useState<PumpRow | null>(null);
  const [showAssetStatusModal, setShowAssetStatusModal] = useState(false);
  const [selectedAssetStatusPump, setSelectedAssetStatusPump] =
    useState<PumpRow | null>(null);

  const [assetStatusRows, setAssetStatusRows] = useState<Record<string, any>[]>(
    [],
  );
  const [assetStatusLoading, setAssetStatusLoading] = useState(false);
  const [pumpInterlocks, setPumpInterlocks] = useState<
    Record<string, PumpInterlockRule[]>
  >({});

  const [fleetGroups, setFleetGroups] = useState<FleetGroup[]>([]);
  const [selectedFleet, setSelectedFleet] = useState<FleetGroup | null>(null);
  const [showSidebar, setShowSidebar] = useState(sidebarOpenForViewport);

  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  const apiUrl = backendUrl.endsWith("/api") ? backendUrl : `${backendUrl}/api`;

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (typeof value === "number") {
      return Number.isInteger(value) ? value : value.toFixed(2);
    }
    return value;
  };

  const normalizeInterlockChannel = (channel: string) => {
    const key = String(channel || "")
      .trim()
      // Convert camelCase to snake_case.
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .toLowerCase()
      .replace(/[\s-]+/g, "_")
      .replace(/__+/g, "_");

    const aliases: Record<string, string> = {
      engine_coolant_temp: "engine_temp",
      engine_coolant_temperature: "engine_temp",
      engine_temp: "engine_temp",

      engine_oil_pressure: "engine_oil_pressure",
      engine_oil_press: "engine_oil_pressure",
      engine_oil_pressure_psi: "engine_oil_pressure",
      oil_pressure: "engine_oil_pressure",
      eng_oil_pressure: "engine_oil_pressure",

      rpm: "rpm",
      engine_rpm: "rpm",

      powerend_lube_pressure: "powerend_lube_pressure",
      power_end_lube_pressure: "powerend_lube_pressure",
      pe_lube_pressure: "powerend_lube_pressure",
      pe_lube_press: "powerend_lube_pressure",

      powerend_oil_pressure: "powerend_oil_pressure",
      power_end_oil_pressure: "powerend_oil_pressure",
      pe_pressure: "powerend_oil_pressure",

      powerend_oil_temperature: "powerend_oil_temperature",
      power_end_oil_temperature: "powerend_oil_temperature",
      pe_temp: "powerend_oil_temperature",

      discharge_pressure: "discharge_pressure",
      suction_pressure: "suction_pressure",
    };

    return aliases[key] || key;
  };

  const getInterlockChannelValue = (rowData: PumpRow, channel: string): any => {
    const normalizedChannel = normalizeInterlockChannel(channel);

    const valueMap: Record<string, any> = {
      engine_temp: rowData.engine_temp ?? rowData.engine_coolant_temp,

      engine_oil_pressure: rowData.engine_oil_pressure,

      rpm: rowData.rpm,

      powerend_lube_pressure: rowData.powerend_lube_pressure,

      powerend_oil_pressure: rowData.powerend_oil_pressure,

      powerend_oil_temperature: rowData.powerend_oil_temperature,

      suction_pressure: rowData.suction_pressure,

      discharge_pressure: rowData.discharge_pressure,
    };

    return valueMap[normalizedChannel] ?? (rowData as any)[normalizedChannel];
  };

  const compareThreshold = (
    value: any,
    operator?: string,
    limit?: number,
  ): boolean => {
    const numericValue = Number(value);

    if (Number.isNaN(numericValue) || limit === null || limit === undefined) {
      return false;
    }

    switch (operator) {
      case ">":
        return numericValue > limit;
      case ">=":
        return numericValue >= limit;
      case "<":
        return numericValue < limit;
      case "<=":
        return numericValue <= limit;
      case "=":
      case "==":
        return numericValue === limit;
      default:
        return false;
    }
  };

  const isInterlockRuleActive = (
    rowData: PumpRow,
    conditions?: PumpInterlockCondition[],
  ): boolean => {
    if (!conditions?.length) return true;

    return conditions.every((condition) => {
      return compareThreshold(
        getInterlockChannelValue(rowData, condition.channel),
        condition.operator,
        condition.value,
      );
    });
  };

  const getThresholdSeverity = (
    value: any,
    channel: string,
    rowData: PumpRow,
  ): ThresholdSeverity => {
    const normalizedChannel = normalizeInterlockChannel(channel);
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return "normal";
    }

    /*
     * Engine oil pressure should only be evaluated while the engine
     * is running. This prevents zero-pressure values from being
     * highlighted when the pump is stopped.
     */
    if (normalizedChannel === "engine_oil_pressure") {
      const rpm = Number(rowData.rpm ?? 0);

      if (!Number.isFinite(rpm) || rpm < ENGINE_RUNNING_RPM) {
        return "normal";
      }
    }

    const rules = pumpInterlocks[normalizedChannel] || [];

    const applicableRules = rules.filter((rule) =>
      isInterlockRuleActive(rowData, rule.conditions),
    );

    const hasCritical = applicableRules.some(
      (rule) =>
        String(rule.severity).toLowerCase() === "critical" &&
        compareThreshold(numericValue, rule.operator, rule.value),
    );

    if (hasCritical) {
      return "critical";
    }

    const hasWarning = applicableRules.some(
      (rule) =>
        String(rule.severity).toLowerCase() === "warning" &&
        compareThreshold(numericValue, rule.operator, rule.value),
    );

    if (hasWarning) {
      return "warning";
    }

    /*
     * Fallback for engine oil pressure when the backend does not
     * return a matching interlock rule.
     *
     * <= kick-out threshold       : critical
     * <= kick-out threshold + 4   : warning
     * above that                  : normal
     */
    if (
      normalizedChannel === "engine_oil_pressure" &&
      applicableRules.length === 0
    ) {
      if (numericValue <= ENGINE_OIL_KICKOUT_PSI) {
        return "critical";
      }

      if (
        numericValue <=
        ENGINE_OIL_KICKOUT_PSI + ENGINE_OIL_WARNING_MARGIN_PSI
      ) {
        return "warning";
      }
    }

    return "normal";
  };

  const getThresholdCellStyle = (
    value: any,
    channel: string,
    rowData: PumpRow,
  ): React.CSSProperties => {
    const severity = getThresholdSeverity(value, channel, rowData);

    if (severity === "critical") {
      return {
        backgroundColor: "#f8d7da",
        boxShadow: "inset 0 0 0 9999px #f8d7da",
        color: "#842029",
        fontWeight: 700,
      };
    }

    if (severity === "warning") {
      return {
        backgroundColor: "#fff3cd",
        boxShadow: "inset 0 0 0 9999px #fff3cd",
        color: "#856404",
        fontWeight: 700,
      };
    }

    return {};
  };

  const normalizeTimestamp = (ts: number) => {
    return ts < 1000000000000 ? ts * 1000 : ts;
  };
  const getPumpStatus = (
    pump: any,
    pumpTimestamp?: number | null,
  ): "Active" | "Inactive" | "Lost Comm" => {
    if (
      !pumpTimestamp ||
      Date.now() - normalizeTimestamp(pumpTimestamp) > LOST_COMM_MS
    ) {
      return "Lost Comm";
    }

    const rpm = Number(pump.rpm ?? 0);
    const load = Number(pump.engine_load ?? 0);
    const pressure = Number(pump.discharge_pressure ?? 0);

    if (rpm > 0 || load > 0 || pressure > 0) {
      return "Active";
    }

    return "Inactive";
  };

  const getControllerType = (pump: any): number | null => {
    const raw =
      pump.controller_type ??
      pump.Controller_Type ??
      pump.controllerType ??
      null;

    if (raw === null || raw === undefined || raw === "") return null;

    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getPumpType = (pump: any): "diesel" | "electric" => {
    const controllerType = getControllerType(pump);

    if (controllerType === 101) {
      return "electric";
    }
    return "diesel";
  };

  const getBackendStatus = (
    pump: any,
    pumpTimestamp?: number | null,
  ): string => {
    return (
      pump.status ??
      pump.pump_status ??
      pump.stage_status ??
      getPumpStatus(pump, pumpTimestamp)
    );
  };

  const openAssetStatus = async (pump: PumpRow) => {
    try {
      setAssetStatusPump(pump);
      setAssetStatusLoading(true);
      setAssetStatusRows([]);

      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");

      const response = await axios.get(
        `${apiUrl}/asset-status/${encodeURIComponent(pump.pump_name)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const assetData = response.data?.data;

      setAssetStatusRows(assetData ? [assetData] : []);
    } catch (error) {
      console.error("Error fetching asset status:", error);
      setAssetStatusRows([]);
    } finally {
      setAssetStatusLoading(false);
    }
  };

  const fetchFleetGroups = useCallback(async () => {
    try {
      setLoading(true);

      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");
      if (!token) {
        setErrorMessage("Authentication token missing. Please sign in again.");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${backendUrl}/pads`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const padsData: Pad[] = response.data || [];
      const fleetMap = new Map<number, Pad>();
      const isActive = (pad: Pad) =>
        (pad.status || "").toLowerCase() === "active";

      padsData.forEach((pad) => {
        if (!pad.fleet) return;

        const existing = fleetMap.get(pad.fleet);
        if (!existing) {
          fleetMap.set(pad.fleet, pad);
          return;
        }

        const padActive = isActive(pad);
        const existingActive = isActive(existing);

        // Prefer Active pads. If both are Active (or both inactive),
        // keep the one with the more recent start_ts.
        if (padActive && !existingActive) {
          fleetMap.set(pad.fleet, pad);
        } else if (padActive === existingActive) {
          if ((pad.start_ts ?? 0) > (existing.start_ts ?? 0)) {
            fleetMap.set(pad.fleet, pad);
          }
        }
      });

      const groups: FleetGroup[] = Array.from(fleetMap.entries())
        .map(([fleet, latestPad]) => ({ fleet, latestPad }))
        .sort((a, b) => a.fleet - b.fleet);

      setFleetGroups(groups);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching fleet groups:", error);
      setErrorMessage("Failed to load fleets.");
      setLoading(false);
    }
  }, [backendUrl]);

  const fetchPumpConfigs = useCallback(
    async (pumpNames: string[]) => {
      if (!pumpNames.length) return new Map<string, any>();

      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");

      if (!token) return new Map<string, any>();

      const response = await axios.post(
        `${backendUrl}/pump-config/latest`,
        { pump_names: pumpNames },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const configs = response.data?.configs || [];

      return new Map(
        configs.map((config: any) => [
          String(config.pump_name).trim().toUpperCase(),
          config,
        ]),
      );
    },
    [backendUrl],
  );

  const fetchPumpInterlocks = useCallback(async () => {
    try {
      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");

      if (!token) return;

      const response = await axios.get(`${backendUrl}/pump-interlocks/latest`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Raw pump interlock response:", response.data);

      const rawRules = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.rules)
          ? response.data.rules
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];

      const normalizedRules: PumpInterlockRule[] = rawRules
        .map((rule: any) => {
          const channel =
            rule.channel ??
            rule.channel_name ??
            rule.parameter ??
            rule.tag_name ??
            rule.sensor_name;

          const operator =
            rule.operator ??
            rule.comparison_operator ??
            rule.condition_operator ??
            rule.threshold_operator;

          const rawValue =
            rule.value ??
            rule.threshold ??
            rule.threshold_value ??
            rule.limit_value ??
            rule.setpoint;

          const severity = String(
            rule.severity ??
              rule.alarm_severity ??
              rule.level ??
              rule.priority ??
              "",
          ).toLowerCase();

          const value = Number(rawValue);

          if (
            !channel ||
            !operator ||
            Number.isNaN(value) ||
            !["warning", "critical"].includes(severity)
          ) {
            console.warn("Skipping invalid interlock rule:", rule);
            return null;
          }

          const conditions = Array.isArray(rule.conditions)
            ? (rule.conditions
                .map((condition: any) => {
                  const conditionChannel =
                    condition.channel ??
                    condition.channel_name ??
                    condition.parameter;

                  const conditionOperator =
                    condition.operator ?? condition.comparison_operator;

                  const conditionValue = Number(
                    condition.value ??
                      condition.threshold ??
                      condition.threshold_value,
                  );

                  if (
                    !conditionChannel ||
                    !conditionOperator ||
                    Number.isNaN(conditionValue)
                  ) {
                    return null;
                  }

                  return {
                    channel: conditionChannel,
                    operator: conditionOperator,
                    value: conditionValue,
                  };
                })
                .filter(Boolean) as PumpInterlockCondition[])
            : [];

          return {
            channel,
            operator,
            value,
            severity: severity as "warning" | "critical",
            conditions,
          };
        })
        .filter(Boolean) as PumpInterlockRule[];

      const groupedRules = normalizedRules.reduce<
        Record<string, PumpInterlockRule[]>
      >((acc, rule) => {
        const key = normalizeInterlockChannel(rule.channel);

        acc[key] = [...(acc[key] || []), rule];

        return acc;
      }, {});

      console.log("Normalized pump interlocks:", normalizedRules);
      console.log("Grouped pump interlocks:", groupedRules);

      setPumpInterlocks(groupedRules);
    } catch (error) {
      console.error("Error fetching pump interlocks:", error);
      setPumpInterlocks({});
    }
  }, [backendUrl]);

  const fetchPumpDashboardData = useCallback(
    async (fleetNumber: number) => {
      try {
        setErrorMessage("");

        if (pumpRows.length === 0) {
          setLoading(true);
        }

        const token =
          localStorage.getItem("jwt") || localStorage.getItem("token");
        if (!token) {
          setErrorMessage(
            "Authentication token missing. Please sign in again.",
          );
          setLoading(false);
          return;
        }
        const fleetsParam = String(fleetNumber);

        const response = await axios.get(
          `${backendUrl}/latest-treatment-pump-data-multi?fleets=${fleetsParam}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const data = response.data || {};

        const pumpNamesSet = new Set<string>();

        [fleetNumber].forEach((fleet) => {
          const fleetData = data[fleet] || data[String(fleet)];

          if (!fleetData?.pumps?.length) return;

          fleetData.pumps.forEach((pump: any) => {
            const pumpName = pump.pump_name ?? pump.unit_number;
            if (pumpName) pumpNamesSet.add(pumpName);
          });
        });

        const pumpConfigMap = await fetchPumpConfigs(Array.from(pumpNamesSet));

        const rows: PumpRow[] = [];

        [fleetNumber].forEach((fleet) => {
          const fleetData = data[fleet] || data[String(fleet)];

          if (!fleetData?.pumps?.length) return;

          const pumpTimestamp = fleetData.pump_timestamp ?? null;

          fleetData.pumps.forEach((pump: any) => {
            const pumpName = pump.pump_name ?? pump.unit_number;
            const pumpConfig = pumpConfigMap.get(
              String(pumpName).trim().toUpperCase(),
            );
            const mergedPump = {
              ...pump,
              controller_type:
                pumpConfig?.controller_type ?? pump.controller_type,
            };
            rows.push({
              fleet,
              pump_name: pumpName,
              controller_type: getControllerType(mergedPump),
              pump_type: getPumpType(mergedPump),
              station:
                pump.station ??
                pump.station_number ??
                pump.station_id ??
                pump.station_num,
              engine_hours: pump.engine_hours,
              trans_hours: pump.trans_hours ?? pump.transmission_hours,
              fe_hours: pump.fe_hours,
              throttle: pump.throttle,
              rpm: pump.rpm ?? pump.engine_rpm,
              engine_coolant_temp: pump.engine_coolant_temp ?? pump.engine_temp,
              engine_temp: pump.engine_temp ?? pump.engine_coolant_temp,
              engine_oil_pressure: pump.engine_oil_pressure,
              powerend_lube_pressure:
                pump.powerend_lube_pressure ??
                pump.power_end_lube_pressure ??
                pump.pe_lube_pressure ??
                pump.pe_lube_press,
              powerend_oil_pressure:
                pump.powerend_oil_pressure ?? pump.pe_pressure,
              powerend_oil_temperature: pump.pe_temp,
              engine_oil_temperature:
                pump.engine_oil_temperature ??
                pump.engine_oil_temp ??
                pump.engineoiltemperature,
              engine_fuel_rate: pump.engine_fuel_rate,
              engine_load: pump.engine_load,
              trans_gear: pump.trans_gear ?? pump.gear,
              trans_oil_pressure:
                pump.trans_oil_pressure ?? pump.trans_pressure,
              trans_oil_temp: pump.trans_oil_temp ?? pump.trans_temp,
              trans_output_rpm: pump.trans_output_rpm,
              suction_pressure:
                pump.suction_pressure ??
                pump.atlas_secondary_pump_suction_pressure,
              discharge_pressure: pump.discharge_pressure,
              df_substitution: pump.df_substitution ?? pump.dual_fuel_ratio,
              df_displacement: pump.df_displacement ?? pump.inst_fuel_disp,
              calculated_discharge_rate: pump.calculated_discharge_rate,
              motor_rpm: pump.motor_rpm ?? pump.vfd_rpm,
              dc_link_volts: pump.dc_link_volts ?? pump.vfd_dc_link_volts,
              motor_current: pump.motor_current ?? pump.vfd_motor_current,
              winding_a_temp: pump.winding_a_temp,
              winding_b_temp: pump.winding_b_temp,
              winding_c_temp: pump.winding_c_temp,
              de_bearing_temp: pump.de_bearing_temp,
              nde_bearing_temp: pump.nde_bearing_temp,
              target_rate: pump.target_rate,
              igbt_1_temp: pump.igbt_1_temp ?? pump.vfd_igbt_1_temp,
              igbt_2_temp: pump.igbt_2_temp ?? pump.vfd_igbt_2_temp,
              pump_timestamp: pumpTimestamp,
              status: getBackendStatus(mergedPump, pumpTimestamp),
            });
          });
        });

        rows.sort((a, b) => {
          if (a.fleet !== b.fleet) return a.fleet - b.fleet;
          return Number(a.station ?? 0) - Number(b.station ?? 0);
        });

        setPumpRows(rows);

        const latestPumpTimestamp = rows
          .map((row) => row.pump_timestamp)
          .filter((ts): ts is number => typeof ts === "number")
          .sort((a, b) => b - a)[0];

        setLastUpdated(
          latestPumpTimestamp
            ? new Date(normalizeTimestamp(latestPumpTimestamp))
            : new Date(),
        );

        setLoading(false);
      } catch (error) {
        console.error("Error fetching Pump Dashboard data:", error);
        setErrorMessage("Failed to load pump dashboard data.");
        setLoading(false);
      }
    },
    [backendUrl, fetchPumpConfigs, pumpRows.length],
  );
  useEffect(() => {
    fetchFleetGroups();
  }, [fetchFleetGroups]);

  useEffect(() => {
    fetchPumpInterlocks();
  }, [fetchPumpInterlocks]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fleetParam = params.get("fleet");

    if (!fleetParam || fleetGroups.length === 0) return;

    const matchedFleet = fleetGroups.find(
      (f) => String(f.fleet) === fleetParam,
    );

    if (matchedFleet) {
      setSelectedFleet(matchedFleet);
    }
  }, [location.search, fleetGroups]);

  useEffect(() => {
    if (!selectedFleet) return;

    fetchPumpDashboardData(selectedFleet.fleet);

    const interval = setInterval(() => {
      fetchPumpDashboardData(selectedFleet.fleet);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [selectedFleet, fetchPumpDashboardData]);

  const filteredFleetGroups = useMemo(() => {
    const q = fleetSearchText.trim().toLowerCase();
    if (!q) return fleetGroups;

    return fleetGroups.filter((group) => {
      const pad = group.latestPad;
      return [
        `Fleet ${group.fleet}`,
        group.fleet,
        pad?.pad_name,
        pad?.status,
        pad?.well_count,
        pad?.stage_count,
        pad?.start_ts ? new Date(pad.start_ts).toLocaleDateString() : null,
      ]
        .map((value) => formatValue(value))
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [fleetGroups, fleetSearchText]);

  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return pumpRows;

    return pumpRows.filter((row) =>
      Object.values(row)
        .map((value) => formatValue(value))
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [pumpRows, searchText]);

  const dieselData = useMemo(
    () => filteredData.filter((row) => row.pump_type !== "electric"),
    [filteredData],
  );

  const electricData = useMemo(
    () => filteredData.filter((row) => row.pump_type === "electric"),
    [filteredData],
  );

  const blenderPreviewRows = useMemo(
    () => (selectedFleet ? buildBlenderDashboardPreviewRows() : []),
    [selectedFleet],
  );

  const formatLastUpdated = (value: Date | null) => {
    if (!value) return "--";
    return value.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusClass = (status: string) => {
    const normalized = status?.toLowerCase();

    if (normalized === "active" || normalized === "complete") {
      return styles.statusActive;
    }

    if (normalized === "lost comm" || normalized === "expired") {
      return styles.statusLost;
    }

    return styles.statusInactive;
  };

  const handleRowClick = (pump: PumpRow) => {
    navigate(
      `/fleet?fleet=${encodeURIComponent(
        String(pump.fleet),
      )}&pump=${encodeURIComponent(pump.pump_name)}`,
    );
  };

  const handleBlenderRowClick = (blender: BlenderDashboardPreviewRow) => {
    navigate(
      `/fleet?tab=blender&blender=${encodeURIComponent(blender.agent_name)}`,
    );
  };

  return (
    <section
      className={`${styles.section} ${
        theme === "dark" ? "dark-theme" : "light-theme"
      }`}
    >
      <Header setShowSidebar={setShowSidebar} showSidebar={showSidebar} />

      <div className={styles.container}>
        <div
          className={styles.sidebarStyle}
          style={{
            width: showSidebar ? "224px" : "56px",
            transition: "width 0.3s",
          }}
        >
          <Sidebar />
        </div>

        <main className={styles.mainContentStyle}>
          <div className={styles.pageHeader}>
            <h1 className={styles.title}>Pump Dashboard</h1>

            <div className={styles.liveMeta}>
              <span className={styles.liveDot} />
              <span>Live • Auto refresh every 2 sec</span>
              <>
                <span className={styles.metaDivider}>|</span>
                <span>Last updated: {formatLastUpdated(lastUpdated)}</span>
              </>
            </div>
          </div>
          {!selectedFleet && (
            <section className={styles.tableCard}>
              <div className={styles.tableHeaderRow}>
                <div>
                  <h2 className={styles.cardTitle}>Fleets</h2>
                  <p className={styles.cardSubtitle}>
                    Select a fleet to view live pump measurements and fleet
                    status.
                  </p>
                </div>
                <div className={styles.searchBox}>
                  <input
                    className={styles.searchInput}
                    value={fleetSearchText}
                    onChange={(e) => setFleetSearchText(e.target.value)}
                    placeholder="Search fleet, pad, status..."
                    aria-label="Search fleets"
                  />

                  <div className={styles.searchMeta}>
                    <span className={styles.searchCount}>
                      {filteredFleetGroups.length}/{fleetGroups.length}
                    </span>

                    {fleetSearchText && (
                      <button
                        type="button"
                        className={styles.searchClear}
                        onClick={() => setFleetSearchText("")}
                        aria-label="Clear fleet search"
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fleet</th>
                      <th>Latest Pad</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Wells</th>
                      <th>Stages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td className={styles.emptyCell} colSpan={6}>
                          Loading fleets...
                        </td>
                      </tr>
                    ) : filteredFleetGroups.length > 0 ? (
                      filteredFleetGroups.map((group) => (
                        <tr
                          key={group.fleet}
                          className={styles.fleetRow}
                          onClick={() => {
                            setSelectedFleet(group);
                            setPumpRows([]);
                            setSearchText("");
                            setLoading(true);
                          }}
                          title="Click to view pumps for this fleet"
                        >
                          <td className={styles.unitCell}>
                            Fleet {group.fleet}
                          </td>
                          <td>{group.latestPad.pad_name || "N/A"}</td>
                          <td>
                            <span
                              className={`${styles.statusPill} ${getStatusClass(group.latestPad.status)}`}
                            >
                              {group.latestPad.status || "N/A"}
                            </span>
                          </td>
                          <td>
                            {group.latestPad.start_ts
                              ? new Date(
                                  group.latestPad.start_ts,
                                ).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td>{formatValue(group.latestPad.well_count)}</td>
                          <td>{formatValue(group.latestPad.stage_count)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className={styles.emptyCell} colSpan={6}>
                          {fleetGroups.length > 0
                            ? "No fleets match your search."
                            : "No fleets available."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          {selectedFleet && (
            <>
              <div className={styles.controlsRow}>
                <button
                  type="button"
                  className={styles.countBadge}
                  onClick={() => {
                    setSelectedFleet(null);
                    setPumpRows([]);
                    setSearchText("");
                    setLastUpdated(null);
                  }}
                >
                  ← Back to Fleets
                </button>
                <div className={styles.searchBox}>
                  <input
                    className={styles.searchInput}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search across all pump telemetry fields..."
                    aria-label="Search pump telemetry"
                  />

                  {searchText && (
                    <div className={styles.searchMeta}>
                      <span className={styles.searchCount}>
                        {filteredData.length}/{pumpRows.length}
                      </span>

                      <button
                        type="button"
                        className={styles.searchClear}
                        onClick={() => setSearchText("")}
                        aria-label="Clear pump search"
                        title="Clear search"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.summaryCards}>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Total Pumps</span>
                  <span className={styles.summaryValue}>
                    {dieselData.length}
                  </span>
                </div>

                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Active</span>
                  <span className={styles.summaryValue}>
                    {
                      dieselData.filter(
                        (pump) => pump.status?.toLowerCase() === "active",
                      ).length
                    }
                  </span>
                </div>

                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Lost Comm</span>
                  <span className={styles.summaryValue}>
                    {
                      dieselData.filter(
                        (pump) => pump.status?.toLowerCase() === "lost comm",
                      ).length
                    }
                  </span>
                </div>

                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Inactive</span>
                  <span className={styles.summaryValue}>
                    {
                      dieselData.filter(
                        (pump) => pump.status?.toLowerCase() === "inactive",
                      ).length
                    }
                  </span>
                </div>
              </div>
              <section className={styles.tableCard}>
                <div className={styles.tableHeaderRow}>
                  <div>
                    <h2 className={styles.cardTitle}>
                      Fleet {selectedFleet?.fleet} Diesel Pumps
                    </h2>
                    <p className={styles.cardSubtitle}>
                      {selectedFleet?.latestPad?.pad_name} • Near real-time
                      snapshot of latest pump measurements.
                    </p>
                  </div>

                  <div className={styles.countBadge}>
                    {dieselData.length} pumps
                  </div>
                </div>

                {errorMessage && (
                  <div className={styles.errorBanner}>{errorMessage}</div>
                )}

                <div
                  className={`${styles.tableScroll} ${styles.pumpTableScroll}`}
                >
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Unit Number</th>
                        <th>Station #</th>
                        <th>Engine Hours</th>
                        <th>Trans Hours</th>
                        <th>FE Hours</th>
                        <th>Power End Lube Pressure</th>
                        <th>Powerend Oil Pressure</th>
                        <th>Powerend Oil Temperature</th>
                        <th>Throttle</th>
                        <th>Engine RPM</th>
                        <th>Engine Coolant Temperature</th>
                        <th>Oil Pressure</th>
                        <th>Engine Load</th>
                        <th>Trans Gear</th>
                        <th>Trans Oil Pressure</th>
                        <th>Trans Oil Temp</th>
                        <th>Suction Pressure</th>
                        <th>Discharge Pressure</th>
                        <th>DF Substitution</th>
                        <th>DF Displacement</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr>
                          <td className={styles.emptyCell} colSpan={22}>
                            Loading pump dashboard data...
                          </td>
                        </tr>
                      ) : dieselData.length > 0 ? (
                        dieselData.map((row) => (
                          <tr
                            key={`${row.fleet}-${row.pump_name}`}
                            className={styles.fleetRow}
                            onClick={() => handleRowClick(row)}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              openAssetStatus(row);
                            }}
                            title="Click to view historical data. Right-click for Asset Status"
                          >
                            <td className={styles.unitCell}>{row.pump_name}</td>
                            <td>{formatValue(row.station)}</td>
                            <td>{formatValue(row.engine_hours)}</td>
                            <td>{formatValue(row.trans_hours)}</td>
                            <td>{formatValue(row.fe_hours)}</td>
                            <td
                              style={getThresholdCellStyle(
                                row.powerend_lube_pressure,
                                "powerend_lube_pressure",
                                row,
                              )}
                            >
                              {formatValue(row.powerend_lube_pressure)}
                            </td>
                            <td
                              style={getThresholdCellStyle(
                                row.powerend_oil_pressure,
                                "powerend_oil_pressure",
                                row,
                              )}
                            >
                              {formatValue(row.powerend_oil_pressure)}
                            </td>
                            <td
                              style={getThresholdCellStyle(
                                row.powerend_oil_temperature,
                                "powerend_oil_temperature",
                                row,
                              )}
                            >
                              {formatValue(row.powerend_oil_temperature)}
                            </td>
                            <td>{formatValue(row.throttle)}</td>
                            <td
                              style={getThresholdCellStyle(row.rpm, "rpm", row)}
                            >
                              {formatValue(row.rpm)}
                            </td>

                            <td
                              style={getThresholdCellStyle(
                                row.engine_coolant_temp,
                                "engine_temp",
                                row,
                              )}
                            >
                              {formatValue(row.engine_coolant_temp)}
                            </td>

                            <td
                              style={{
                                backgroundColor:
                                  Number(row.engine_oil_pressure) <= 60
                                    ? "#f8d7da"
                                    : Number(row.engine_oil_pressure) <= 64
                                      ? "#fff3cd"
                                      : undefined,

                                color:
                                  Number(row.engine_oil_pressure) <= 60
                                    ? "#842029"
                                    : Number(row.engine_oil_pressure) <= 64
                                      ? "#856404"
                                      : undefined,

                                fontWeight:
                                  Number(row.engine_oil_pressure) <= 64
                                    ? 800
                                    : undefined,
                              }}
                            >
                              {formatValue(row.engine_oil_pressure)}
                            </td>
                            <td>{formatValue(row.engine_load)}</td>
                            <td>{formatValue(row.trans_gear)}</td>
                            <td>{formatValue(row.trans_oil_pressure)}</td>
                            <td>{formatValue(row.trans_oil_temp)}</td>
                            <td
                              style={getThresholdCellStyle(
                                row.suction_pressure,
                                "suction_pressure",
                                row,
                              )}
                            >
                              {formatValue(row.suction_pressure)}
                            </td>

                            <td
                              style={getThresholdCellStyle(
                                row.discharge_pressure,
                                "discharge_pressure",
                                row,
                              )}
                            >
                              {formatValue(row.discharge_pressure)}
                            </td>
                            <td>{formatValue(row.df_substitution)}</td>
                            <td>{formatValue(row.df_displacement)}</td>
                            <td>
                              <span
                                className={`${styles.statusPill} ${getStatusClass(row.status)}`}
                              >
                                {row.status}
                              </span>
                            </td>
                            <td>
                              <button
                                className={styles.assetStatusBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAssetStatus(row);
                                }}
                              >
                                Asset Status
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className={styles.emptyCell} colSpan={22}>
                            No diesel pump telemetry currently available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
              <div className={styles.summaryCards}>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Total Pumps</span>
                  <span className={styles.summaryValue}>
                    {electricData.length}
                  </span>
                </div>

                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Active</span>
                  <span className={styles.summaryValue}>
                    {
                      electricData.filter(
                        (pump) => pump.status?.toLowerCase() === "active",
                      ).length
                    }
                  </span>
                </div>

                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Lost Comm</span>
                  <span className={styles.summaryValue}>
                    {
                      electricData.filter(
                        (pump) => pump.status?.toLowerCase() === "lost comm",
                      ).length
                    }
                  </span>
                </div>

                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Inactive</span>
                  <span className={styles.summaryValue}>
                    {
                      electricData.filter(
                        (pump) => pump.status?.toLowerCase() === "inactive",
                      ).length
                    }
                  </span>
                </div>
              </div>
              <section className={styles.tableCard}>
                <div className={styles.tableHeaderRow}>
                  <div>
                    <h2 className={styles.cardTitle}>
                      Fleet {selectedFleet?.fleet} Electric Pumps
                    </h2>
                    <p className={styles.cardSubtitle}>
                      {selectedFleet?.latestPad?.pad_name} • Electric pump
                      measurements.
                    </p>
                  </div>

                  <div className={styles.countBadge}>
                    {electricData.length} pumps
                  </div>
                </div>

                <div
                  className={`${styles.tableScroll} ${styles.pumpTableScroll}`}
                >
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Unit Number</th>
                        <th>Station #</th>
                        <th>FE Hours</th>
                        <th>Powerend Oil Pressure</th>
                        <th>Powerend Oil Temperature</th>
                        <th>Motor RPM</th>
                        <th>DC Link Volts</th>
                        <th>Motor Current</th>
                        <th>Winding A Temp</th>
                        <th>Winding B Temp</th>
                        <th>Winding C Temp</th>
                        <th>DE Bearing Temp</th>
                        <th>NDE Bearing Temp</th>
                        <th>Suction Pressure</th>
                        <th>Discharge Pressure</th>
                        <th>Discharge Rate</th>
                        <th>Target Rate</th>
                        <th>IGBT 1 Temp</th>
                        <th>IGBT 2 Temp</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr>
                          <td className={styles.emptyCell} colSpan={21}>
                            Loading electric pump data...
                          </td>
                        </tr>
                      ) : electricData.length > 0 ? (
                        electricData.map((row) => (
                          <tr
                            key={`${row.fleet}-${row.pump_name}-electric`}
                            className={styles.fleetRow}
                            onClick={() => handleRowClick(row)}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              openAssetStatus(row);
                            }}
                            title="Click to view historical data. Right-click for Asset Status"
                          >
                            <td className={styles.unitCell}>{row.pump_name}</td>
                            <td>{formatValue(row.station)}</td>
                            <td>{formatValue(row.fe_hours)}</td>
                            <td
                              style={getThresholdCellStyle(
                                row.powerend_oil_pressure,
                                "powerend_oil_pressure",
                                row,
                              )}
                            >
                              {formatValue(row.powerend_oil_pressure)}
                            </td>

                            <td
                              style={getThresholdCellStyle(
                                row.powerend_oil_temperature,
                                "powerend_oil_temperature",
                                row,
                              )}
                            >
                              {formatValue(row.powerend_oil_temperature)}
                            </td>

                            <td
                              style={getThresholdCellStyle(
                                row.motor_rpm,
                                "motor_rpm",
                                row,
                              )}
                            >
                              {formatValue(row.motor_rpm)}
                            </td>
                            <td>{formatValue(row.dc_link_volts)}</td>
                            <td>{formatValue(row.motor_current)}</td>
                            <td>{formatValue(row.winding_a_temp)}</td>
                            <td>{formatValue(row.winding_b_temp)}</td>
                            <td>{formatValue(row.winding_c_temp)}</td>
                            <td>{formatValue(row.de_bearing_temp)}</td>
                            <td>{formatValue(row.nde_bearing_temp)}</td>

                            <td
                              style={getThresholdCellStyle(
                                row.suction_pressure,
                                "suction_pressure",
                                row,
                              )}
                            >
                              {formatValue(row.suction_pressure)}
                            </td>

                            <td
                              style={getThresholdCellStyle(
                                row.discharge_pressure,
                                "discharge_pressure",
                                row,
                              )}
                            >
                              {formatValue(row.discharge_pressure)}
                            </td>

                            <td
                              style={getThresholdCellStyle(
                                row.calculated_discharge_rate,
                                "calculated_discharge_rate",
                                row,
                              )}
                            >
                              {formatValue(row.calculated_discharge_rate)}
                            </td>
                            <td>{formatValue(row.target_rate)}</td>
                            <td>{formatValue(row.igbt_1_temp)}</td>
                            <td>{formatValue(row.igbt_2_temp)}</td>
                            <td>
                              <span
                                className={`${styles.statusPill} ${getStatusClass(row.status)}`}
                              >
                                {row.status}
                              </span>
                            </td>
                            <td>
                              <button
                                className={styles.assetStatusBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAssetStatus(row);
                                }}
                              >
                                Asset Status
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className={styles.emptyCell} colSpan={21}>
                            No electric pump data currently available for this
                            fleet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
          {selectedFleet && (
            <>
              <div className={styles.blenderDashboardDivider}>
                <span>Blenders</span>
              </div>

              <div className={styles.summaryCards}>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Total Blenders</span>
                  <span className={styles.summaryValue}>
                    {blenderPreviewRows.length}
                  </span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Active</span>
                  <span className={styles.summaryValue}>
                    {
                      blenderPreviewRows.filter(
                        (row) => row.status === "Active",
                      ).length
                    }
                  </span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Diesel</span>
                  <span className={styles.summaryValue}>
                    {
                      blenderPreviewRows.filter(
                        (row) => row.blender_type === "Diesel",
                      ).length
                    }
                  </span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryLabel}>Electric</span>
                  <span className={styles.summaryValue}>
                    {
                      blenderPreviewRows.filter(
                        (row) => row.blender_type === "Electric",
                      ).length
                    }
                  </span>
                </div>
              </div>

              <section
                className={`${styles.tableCard} ${styles.blenderPreviewCard}`}
              >
                <div className={styles.tableHeaderRow}>
                  <div>
                    <h2 className={styles.cardTitle}>
                      Fleet {selectedFleet.fleet} Blenders
                    </h2>
                    <p className={styles.cardSubtitle}>
                      {selectedFleet.latestPad?.pad_name} • Blender measurements
                      and status.
                    </p>
                  </div>
                  <div className={styles.blenderPreviewBadge}>
                    {blenderPreviewRows.length} blenders
                  </div>
                </div>

                <div
                  className={`${styles.tableScroll} ${styles.pumpTableScroll}`}
                >
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Type</th>
                        <th>Density</th>
                        <th>Discharge Rate</th>
                        <th>Suction Rate</th>
                        <th>Auger RPM</th>
                        <th>Status</th>
                        <th>Last Update</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blenderPreviewRows.map((row) => (
                        <tr
                          key={row.agent_name}
                          className={styles.fleetRow}
                          onClick={() => handleBlenderRowClick(row)}
                          title="Click to view Blender historical data"
                        >
                          <td className={styles.unitCell}>{row.agent_name}</td>
                          <td>{row.blender_type}</td>
                          <td>{row.density.toFixed(1)} ppg</td>
                          <td>{row.discharge_rate.toFixed(1)} bpm</td>
                          <td>{row.suction_rate.toFixed(1)} bpm</td>
                          <td>{row.auger_rpm}</td>
                          <td>
                            <span
                              className={`${styles.statusPill} ${getStatusClass(row.status)}`}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td>{row.last_updated}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
          {assetStatusPump && (
            <div className={styles.assetStatusOverlay}>
              <div className={styles.assetStatusModal}>
                <div className={styles.assetStatusHeader}>
                  <h3>Asset Status - {assetStatusPump?.pump_name}</h3>

                  <button
                    type="button"
                    className={styles.assetStatusClose}
                    onClick={() => {
                      setAssetStatusPump(null);
                      setShowAssetStatusModal(false);
                    }}
                  >
                    ×
                  </button>
                </div>

                {assetStatusLoading ? (
                  <p className={styles.assetStatusMessage}>
                    Loading asset status...
                  </p>
                ) : assetStatusRows.length === 0 ? (
                  <p className={styles.assetStatusMessage}>
                    No asset status found for this pump.
                  </p>
                ) : (
                  <div className={styles.assetStatusTableWrap}>
                    <table className={styles.assetStatusTable}>
                      <tbody>
                        {Object.entries(assetStatusRows[0])
                          .filter(([, value]) => value !== null && value !== "")
                          .map(([key, value]) => (
                            <tr key={key}>
                              <th>{key.replace(/([A-Z])/g, " $1").trim()}</th>
                              <td>{String(value)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </section>
  );
};

export default PumpDashboard;
